import { useState, useCallback } from "react";
import { toast } from "sonner";
import { useGlobalContext } from "../../GlobalContext";

export default function useVolunteers() {
  const { api, AdminId } = useGlobalContext();

  const [volunteer, setVolunteer] = useState([]);
  const [loading, setLoading] = useState(false);

  const shouldShowLocalError = (data) => {
    return (
      data?.responseCode !== "00" &&
      data?.responseCode !== "02" &&
      data?.responseCode !== "03" &&
      data?.responseCode !== "98" &&
      data?.responseCode !== "401"
    );
  };

  const getVolunteerUserId = () => {
    try {
      const volunteerData = JSON.parse(
        sessionStorage.getItem("volunteerData") || "{}"
      );

      return String(
        volunteerData?.userId ||
          volunteerData?.userid ||
          volunteerData?.id ||
          AdminId ||
          ""
      );
    } catch (error) {
      return String(AdminId || "");
    }
  };

  /* ---------------- FETCH VOLUNTEER PROFILE ---------------- */

  const fetchVolunteerProfile = useCallback(
    async (volunteerId, silent = false) => {
      if (!volunteerId) return [];

      if (!silent) setLoading(true);

      try {
        const res = await api.post(
          "/api/VolunteerUser/v1.0/volunteerProfileDetails",
          {
            volunteerId: String(volunteerId || ""),
          }
        );

        if (res?.data?.responseCode === "00") {
          const profile = res?.data?.response;

          setVolunteer(profile ? [profile] : []);

          return profile ? [profile] : [];
        }

        if (shouldShowLocalError(res?.data)) {
          toast.error(
            res?.data?.responseMessage || "Failed to fetch volunteer profile"
          );
        }

        setVolunteer([]);
        return [];
      } catch (error) {
        console.error("fetchVolunteerProfile error:", error);
        toast.error("Error fetching volunteer profile");
        setVolunteer([]);
        return [];
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [api]
  );

  /* ---------------- CREATE VOLUNTEER ---------------- */

  const createVolunteerApi = async (payload, onSuccess) => {
    try {
      const res = await api.post(
        "/api/VolunteerUser/v1.0/insertVolunteerDetails",
        payload
      );

      if (res?.data?.responseCode === "00") {
        toast.success(res?.data?.responseMessage || "Volunteer added");

        onSuccess?.();

        if (res?.data?.response?.volunteerCode) {
          fetchVolunteerProfile(res.data.response.volunteerCode, true);
        }

        return res.data;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(res?.data?.responseMessage || "Failed to add volunteer");
      }

      return null;
    } catch (error) {
      console.error("createVolunteerApi error:", error);
      toast.error("Error adding volunteer");
      return null;
    }
  };

  /* ---------------- FETCH ALL VOLUNTEERS ---------------- */

  const fetchAllVolunteers = useCallback(
    async (adminId = "", silent = false) => {
      if (!silent) setLoading(true);

      try {
        const res = await api.post(
          "/api/VolunteerAdmin/v1.0/getAllVolunteerDetails",
          {
            // Volunteer panel me adminId ki jagah volunteerData.userId jayegi
            adminId: String(adminId || getVolunteerUserId()),
          }
        );

        if (res?.data?.responseCode === "00") {
          const volunteers = Array.isArray(res?.data?.data)
            ? res.data.data
            : [];

          setVolunteer(volunteers);

          return volunteers;
        }

        if (shouldShowLocalError(res?.data)) {
          toast.error(
            res?.data?.responseMessage || "Failed to fetch volunteer details"
          );
        }

        setVolunteer([]);
        return [];
      } catch (error) {
        console.error("fetchAllVolunteers error:", error);
        toast.error("Error fetching volunteer details");
        setVolunteer([]);
        return [];
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [api, AdminId]
  );

  /* ---------------- REGISTER VOLUNTEER ---------------- */

  const registerVolunteerApi = async (payload, onSuccess) => {
    try {
      const res = await api.post(
        "/api/VolunteerUser/v1.0/registerVolunteer",
        payload
      );

      if (res?.data?.responseCode === "00") {
        toast.success(res?.data?.responseMessage || "Volunteer registered");
        onSuccess?.();

        return res?.data?.response;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Failed to register volunteer"
        );
      }

      return null;
    } catch (error) {
      console.error("registerVolunteerApi error:", error);
      toast.error("Error registering volunteer");
      return null;
    }
  };

  /* ---------------- UPDATE VOLUNTEER DUTY TYPE ---------------- */

  const updateVolunteerDutyTypeApi = async (payload) => {
    try {
      const res = await api.post(
        "/api/VolunteerUser/v1.0/updateVolunteerDutyType",
        payload
      );

      if (res?.data?.responseCode === "00") {
        toast.success(
          res?.data?.responseMessage || "Duty updated successfully"
        );

        return true;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(res?.data?.responseMessage || "Failed to update duty");
      }

      return false;
    } catch (error) {
      console.error("updateVolunteerDutyTypeApi error:", error);
      toast.error("Error updating volunteer duty");
      return false;
    }
  };

  /* ---------------- UPDATE VOLUNTEER DUTY TIME ---------------- */

  const updateVolunteerDutyTimeApi = async (volunteerId, dutyValue) => {
    const distributorId = getVolunteerUserId();

    if (!distributorId) {
      toast.error("Session expired. Please login again.");
      return false;
    }

    try {
      const res = await api.post(
        "/api/Volunteer/v1.0/updateVolunteerDutyTime",
        {
          distributorId: String(distributorId),
          volunteerId: String(volunteerId || ""),
          dutyValue: String(dutyValue || ""),
          device: "",
          userId: "",
          latitude: "",
          longitude: "",
        }
      );

      if (res?.data?.responseCode === "00") {
        toast.success(res?.data?.responseMessage || "Duty status updated");

        return true;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Failed to update duty status"
        );
      }

      return false;
    } catch (error) {
      console.error("updateVolunteerDutyTimeApi error:", error);
      toast.error("Error updating duty status");
      return false;
    }
  };

  return {
    volunteer,
    setVolunteer,
    loading,
    fetchVolunteerProfile,
    fetchAllVolunteers,
    createVolunteerApi,
    registerVolunteerApi,
    updateVolunteerDutyTypeApi,
    updateVolunteerDutyTimeApi,
    getVolunteerUserId,
  };
}